import { getMarketOptionColors } from "../lib/marketOptionColors";
import { formatMarketTokenAmount } from "../lib/marketTokenDisplay.js";
import { formatUtcDateTime } from "../lib/timeDisplay";
import { formatWalletAddress } from "../lib/walletAddress";
import { buildXProfileUrl, normalizeXHandle } from "../lib/xProfile";
import { MarketSocialProofSkeleton } from "./skeletons";

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function safeText(value) {
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  return "";
}

function normalizeOptionLabel(optionLabel, optionId) {
  return safeText(optionLabel) || safeText(optionId) || "Option";
}

function normalizeOptionGroups(optionTopVoters) {
  if (!Array.isArray(optionTopVoters)) return [];
  return optionTopVoters.filter(isRecord).map((group, index) => {
    const optionId = safeText(group.optionId) || `option-${index + 1}`;
    return {
      ...group,
      optionId,
      optionLabel: normalizeOptionLabel(group.optionLabel, optionId),
      voters: Array.isArray(group.voters) ? group.voters.filter(isRecord) : []
    };
  });
}

function normalizeRecentActivity(recentActivity) {
  if (!Array.isArray(recentActivity)) return [];
  return recentActivity.filter(isRecord).map((row, index) => ({
    ...row,
    id: safeText(row.id) || `${safeText(row.walletAddress) || "activity"}-${index + 1}`,
    optionLabel: normalizeOptionLabel(row.optionLabel, row.optionId)
  }));
}

function formatActivityTime(value) {
  if (value === null || value === undefined || value === "") return "Time unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Time unavailable";
  return `${formatUtcDateTime(date)} UTC`;
}

export function MarketIdentity({ item }) {
  const safeHandle = item?.displayKind === "linked_x" || item?.displayKind === "sns_x" ? normalizeXHandle(item.xHandle) : "";
  const xProfileUrl = safeHandle ? buildXProfileUrl(safeHandle) : "";
  if (xProfileUrl) {
    return (
      <a
        href={xProfileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-blue-300 underline-offset-4 hover:text-blue-200 hover:underline"
      >
        {`@${safeHandle}`}
      </a>
    );
  }

  return <span className="font-medium text-zinc-200">{formatWalletAddress(item?.walletAddress) || "Unknown wallet"}</span>;
}

export function MarketSocialProof({ market, socialProof, status = "ready", error = "", expectedOptionCount = 4 }) {
  if ((status === "idle" || status === "loading") && !socialProof) {
    return <MarketSocialProofSkeleton optionCount={expectedOptionCount} />;
  }

  const optionGroups = normalizeOptionGroups(socialProof?.optionTopVoters);
  const recentActivity = normalizeRecentActivity(socialProof?.recentActivity);

  return (
    <section
      className="min-h-[34rem] space-y-4 rounded-2xl border border-zinc-800/80 bg-zinc-950/35 p-3 md:min-h-[24rem]"
      data-cls-measure="market-social-proof"
    >
      <div className="space-y-1">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-zinc-200">Market Social Proof</h3>
        <p className="text-xs text-zinc-400">Top voters by option and the latest confirmed vote activity.</p>
      </div>

      {status === "error" && !socialProof ? (
        <p className="text-sm text-zinc-400">{error || "Market social proof is unavailable right now."}</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(20rem,0.85fr)]">
          <TopVotersByOption market={market || socialProof} optionGroups={optionGroups} />
          <RecentActivity market={market || socialProof} rows={recentActivity} />
        </div>
      )}

      {status === "error" && socialProof ? (
        <p className="text-xs text-zinc-500">Showing cached social proof. Live refresh is temporarily unavailable.</p>
      ) : null}
    </section>
  );
}

function TopVotersByOption({ market, optionGroups }) {
  const groups = Array.isArray(optionGroups) ? optionGroups : [];

  return (
    <section className="space-y-3" data-cls-measure="market-social-proof-top-voters">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Top voters by option</h4>
      <div className="grid gap-3 sm:grid-cols-2">
        {groups.length ? groups.map((group, index) => {
          const optionId = safeText(group?.optionId) || `option-${index + 1}`;
          const optionLabel = normalizeOptionLabel(group?.optionLabel, optionId);
          const voters = Array.isArray(group?.voters) ? group.voters.filter(isRecord) : [];
          const colors = getMarketOptionColors(index);
          return (
            <section key={optionId} className="min-h-[8rem] min-w-0 break-words rounded-xl border border-zinc-800 bg-zinc-950/50 p-3">
              <h5 className="mb-2 min-w-0 break-words text-sm font-semibold" style={{ color: colors.stroke }}>
                {optionLabel}
              </h5>
              {voters.length ? (
                <ol className="space-y-2">
                  {voters.map((voter, voterIndex) => (
                    <li key={`${optionId}-${safeText(voter.walletAddress) || voterIndex}`} className="flex items-center justify-between gap-3 text-sm">
                      <span className="min-w-0 truncate">
                        <span className="mr-2 text-zinc-500">{voterIndex + 1}</span>
                        <MarketIdentity item={voter} />
                      </span>
                      <span className="shrink-0 text-xs text-zinc-300">
                        {formatMarketTokenAmount(voter.totalVotes, market, {
                          units: voter.totalVoteUnits ?? voter.total_vote_units ?? null
                        })}
                      </span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-zinc-500">No confirmed voters yet.</p>
              )}
            </section>
          );
        }) : (
          <section className="min-h-[8rem] rounded-xl border border-zinc-800 bg-zinc-950/50 p-3 sm:col-span-2">
            <p className="text-sm text-zinc-500">No option activity yet.</p>
          </section>
        )}
      </div>
    </section>
  );
}

function RecentActivity({ market, rows }) {
  const safeRows = Array.isArray(rows) ? rows.filter(isRecord) : [];

  return (
    <section className="space-y-3" data-cls-measure="market-social-proof-activity">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Recent activity</h4>
      <div className="max-h-[22rem] min-h-[18rem] overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-950/50">
        {safeRows.length ? (
          <ol className="divide-y divide-zinc-800">
            {safeRows.map((row, index) => (
              <li key={row.id || index} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 px-3 py-2 text-sm">
                <div className="min-w-0">
                  <p className="truncate">
                    <MarketIdentity item={row} />
                    <span className="text-zinc-500"> voted </span>
                    <span className="font-medium text-zinc-200">{row.optionLabel}</span>
                  </p>
                  <p className="text-xs text-zinc-500">{formatActivityTime(row.createdAt)}</p>
                </div>
                <span className="shrink-0 text-xs font-medium text-zinc-300">
                  {formatMarketTokenAmount(row.voteAmount, market, {
                    units: row.voteAmountUnits ?? row.vote_amount_units ?? row.grossVoteUnits ?? row.gross_vote_units ?? null
                  })}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="p-3 text-sm text-zinc-500">No confirmed activity yet.</p>
        )}
      </div>
    </section>
  );
}
